import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/** Lib Imports */
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import MolForm from "@moltech/molui/molform";

/** Components */
import RequisitionHistory from "./components/RequisitionHistory";
import ManpowerAcquisitionForm from "./components/ManpowerAcquisitionForm";

/** Utilities */
import { decryptUrlData } from "../../utils/encryption/CryptoService";
import { getAuthProps } from "../../utils/authentication/authLibrary";
import { SwalService } from "../../utils/common/SwalService";

/** ENUM */
import UserRoles from "../../enums/UserRoles.ENUM";
import JobRequisitionStatus from "../../enums/JobRequisitionStatus";

/** RTK Query */
import {
  useLazyGetDataQuery,
  useLazyGetGridDataQuery,
  usePostDataMutation,
} from "../../app/services/commonApi";

import { useMasterData } from "../../app/hooks/useMasterData";
import { MasterDataCacheURL } from "../../enums/CacheURL";

/** CSS */
import "./Requisition.scss";
import "./ManpowerRequisition.scss"; // New Styles

/** Validation Helper */
const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
};

const validateRequired = (value) => {
  if (isEmpty(value)) {
    return 'This field is required';
  }
  return null;
};

const createValidationRule = (isRequired) => {
  if (!isRequired) return () => null;
  return (value) => validateRequired(value);
};

const initialValues = {
  jobRequisitionId: "",
  noOfPositions: "",
  designation: "",
  department: "",
  expectedSalaryLow: "",
  expectedSalaryHigh: "",
  jobTitle: "",
  jobDescription: "",
  requestedBy: "",
  rejectionReason: "",
  requestingManager: "",
  dateOfRequest: null,
  employmentType: "",
  reportingTo: "",
  reasonForRequest: "",
  requiredQualifications: "",
  requiredExperience: "",
  skills: [],
  budgetAvailability: "No",
  requiredJoiningDate: null,
  urgencyLevel: "",
  requestedBySignature: ""
};

const AddManpowerRequisition = () => {
  const formRef = useRef();
  const user = getAuthProps()?.user;
  const userId = user?.userId;
  const params = useParams();
  const navigate = useNavigate();
  const jobRequisitionId = params.id ? Number.parseInt(decryptUrlData(params.id)) : null;
  const isAdmin = Number(user?.roleId) === UserRoles.ADMIN;
  const [getRequisitionDetailsById, requisitionDetailsResponse] =
    useLazyGetDataQuery();

  const [defaultValue, setDefaultValue] = useState(initialValues);
  const [saveData, { isLoading: isSavingData }] = usePostDataMutation();

  const { data: departmentData } = useMasterData(MasterDataCacheURL.GetAllDepartments);
  const [departmentOption, setDepartmentOption] = useState([]);

  const { data: statusData } = useMasterData(MasterDataCacheURL.GetAllStatus);
  const [statusOptions, setStatusOptions] = useState([]);

  const { data: jobOpeningData } = useMasterData(MasterDataCacheURL.GetPublishedJobOpening);
  const [jobOpening, setJobOpening] = useState([]);

  const [showHistory, setShowHistory] = useState(false);

  const { data: activeTemplates } = useMasterData("/job-templates/GetActiveJobTemplates");
  const [fetchTemplateUse] = useLazyGetDataQuery();
  const [formKey, setFormKey] = useState(0);

  const [triggerGetUsers] = useLazyGetGridDataQuery();
  const [userOptions, setUserOptions] = useState([]);

  const { data: skillData } = useMasterData(MasterDataCacheURL.GetAllSkills);
  const [skillOptions, setSkillOptions] = useState([]);

  const { data: employmentTypeData } = useMasterData(MasterDataCacheURL.GetEmploymentType);
  const [employmentTypeOptions, setEmploymentTypeOptions] = useState([]);

  const isEditMode = !!jobRequisitionId;
  const [createdByUserId, setCreatedByUserId] = useState(null);
  const currentStatus = Number(defaultValue?.status);

  const isInterviewer = Number(user?.roleId) === UserRoles.INTERVIEWER;
  const isHR = Number(user?.roleId) === UserRoles.HR;

  const isRejected = currentStatus === JobRequisitionStatus.REJECTED;
  const isApproved = currentStatus === JobRequisitionStatus.APPROVED;
  const isDraft = currentStatus === JobRequisitionStatus.DRAFT;
  const isPending = currentStatus === JobRequisitionStatus.PENDING_APPROVAL;
  const isOnHold = currentStatus === JobRequisitionStatus.ON_HOLD;
  const currentLevel = Number(defaultValue?.approvalLevel);

  // Edit Permission: Only creator (Manager/Interviewer) can edit. Editable only when Status = Draft or Rejected.
  const isCreator = String(userId) === String(createdByUserId);
  const canEditField = !isEditMode || (isCreator && (isDraft || isRejected));

  const isInterviewerDraft = isInterviewer && isDraft;

  useEffect(() => {
    if (statusData && Array.isArray(statusData)) {
      const formatted = statusData.map((item) => ({
        label: item.requisitionStatusName,
        value: item.requisitionStatusId,
      }));
      setStatusOptions(formatted);
    }
  }, [statusData]);

  useEffect(() => {
    if (jobOpeningData && Array.isArray(jobOpeningData)) {
      const formatted = jobOpeningData.map((item) => ({
        label: item.jobOpeningName,
        value: item.jobOpeningId,
      }));
      setJobOpening(formatted);
    }
  }, [jobOpeningData]);

  useEffect(() => {
    // Fetch Users for Dropdowns
    triggerGetUsers({
      url: "/UserManagement/GetUsers",
      params: {
        pageIndex: 1,
        pageSize: 10
      }
    })
      .unwrap()
      .then((res) => {
        if (res?.data?.data) {
          const formatted = res?.data?.data?.map((item) => ({
            label: `${item.firstName} ${item.lastName}`,
            value: item.userId,
          }));
          setUserOptions(formatted);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skillData && Array.isArray(skillData)) {
      const formatted = skillData.map((item) => ({
        label: item.skillName,
        value: item.skillId,
      }));
      setSkillOptions(formatted);
    }
  }, [skillData]);

  useEffect(() => {
    if (employmentTypeData && Array.isArray(employmentTypeData)) {
      const formatted = employmentTypeData.map((item) => ({
        label: item.employmentType,
        value: item.employmentTypeId,
      }));
      setEmploymentTypeOptions(formatted);
    }
  }, [employmentTypeData]);

  useEffect(() => {
    if (jobRequisitionId) {
      const req = {
        url: "/JobRequisition/GetRequisitionById",
        data: Number(jobRequisitionId),
      };
      getRequisitionDetailsById(req);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobRequisitionId]);

  useEffect(() => {
    const apiData = requisitionDetailsResponse?.data?.data;
    if (apiData) {
      setDefaultValue({
        noOfPositions: apiData.noOfPositions,
        designation: apiData.designation,
        department: apiData.department,
        expectedSalaryLow: apiData.expectedSalaryLow,
        expectedSalaryHigh: apiData.expectedSalaryHigh,
        jobTitle: apiData.jobTitle,
        jobDescription: apiData.jobDescription,
        createdAt: apiData.createdAt,
        status: apiData.status,
        approvalLevel: apiData.approvalLevel,
        jobRequisitionId: apiData.jobRequisitionId,
        jobOpeningId: apiData.jobOpeningId,
        jobOpeningName: apiData.jobOpeningName,
        requestedBy: apiData.requestedBy,
        rejectionReason: apiData.rejectionReason,
        requestingManager: apiData.requestingManager || "",
        dateOfRequest: apiData.dateOfRequest ? new Date(apiData.dateOfRequest) : null,
        employmentType: apiData.employmentType || "",
        reportingTo: apiData.reportingTo || "",
        reasonForRequest: apiData.reasonForRequest || "",
        requiredQualifications: apiData.requiredQualifications || "",
        requiredExperience: apiData.requiredExperience || "",
        skills: apiData.skills || [],
        budgetAvailability: apiData.budgetAvailability || "No",
        requiredJoiningDate: apiData.requiredJoiningDate ? new Date(apiData.requiredJoiningDate) : null,
        urgencyLevel: apiData.urgencyLevel || "",
        requestedBySignature: apiData.requestedBySignature || ""
      });
      setCreatedByUserId(apiData.requestedBy);
    }
  }, [requisitionDetailsResponse]);

  useEffect(() => {
    if (departmentData && Array.isArray(departmentData)) {
      const formatted = departmentData.map((item) => ({
        label: item.departmentName,
        value: item.departmentId,
      }));
      setDepartmentOption(formatted);
    }
  }, [departmentData]);

  const getAdminStatusOptions = () => {
    if (isPending && currentLevel === 3) {
      return statusOptions.filter((s) =>
        [
          JobRequisitionStatus.APPROVED,
          JobRequisitionStatus.REJECTED,
          JobRequisitionStatus.ON_HOLD,
        ].includes(s.value),
      );
    }
    if (isOnHold && currentLevel === 3) {
      return statusOptions.filter((s) =>
        [
          JobRequisitionStatus.REJECTED,
          JobRequisitionStatus.PENDING_APPROVAL,
        ].includes(s.value),
      );
    }
    // Admin can always see all status options for other cases
    return statusOptions;
  };

  const getHRStatusOptions = () => {
    if (isDraft) {
      return statusOptions.filter((s) =>
        [JobRequisitionStatus.PENDING_APPROVAL, JobRequisitionStatus.CANCELLED].includes(s.value),
      );
    }
    if (isPending && currentLevel === 2) {
      return statusOptions.filter((s) =>
        [JobRequisitionStatus.PENDING_APPROVAL, JobRequisitionStatus.ON_HOLD, JobRequisitionStatus.CANCELLED].includes(s.value),
      );
    }
    if (isOnHold && currentLevel === 2) {
      return statusOptions.filter((s) =>
        [JobRequisitionStatus.PENDING_APPROVAL, JobRequisitionStatus.CANCELLED].includes(s.value),
      );
    }
    if (isApproved) {
      return statusOptions.filter((s) => [JobRequisitionStatus.APPROVED].includes(s.value));
    }
    return [];
  };

  const getInterviewerStatusOptions = () => {
    if (isDraft) {
      return statusOptions.filter((s) =>
        [JobRequisitionStatus.PENDING_APPROVAL, JobRequisitionStatus.CANCELLED].includes(s.value),
      );
    }
    if (isRejected) {
      return statusOptions.filter((s) => [JobRequisitionStatus.PENDING_APPROVAL].includes(s.value));
    }
    return [];
  };

  const filteredStatusOptions = (() => {
    if (isAdmin) {
      return getAdminStatusOptions();
    }

    if (isHR) {
      return getHRStatusOptions();
    }

    if (isInterviewer) {
      return getInterviewerStatusOptions();
    }

    // Default case - return empty array if no conditions match
    return [];
  })();

  const addNewRequisition = (data) => {
    saveData({
      url: "/JobRequisition/AddJobRequisition",
      data: data,
    }).then((res) => {
      if (res?.error) {
        SwalService.error("Validation Error", res.error?.message || "Something went wrong!");
      } else {
        SwalService.success("Success", "Manpower Requisition added successfully!");
        navigate("/job-requisition");
      }
    });
  };

  const updateRequisition = (data) => {
    saveData({
      url: "/JobRequisition/UpdateJobRequisition",
      data: data,
    }).then((res) => {
      if (res?.error) {
        SwalService.error("Validation Error", res.error?.message || "Something went wrong!");
      } else {
        SwalService.success("Success", "Manpower Requisition updated successfully!");
        navigate("/job-requisition");
      }
    });
  };

  const updateRequisitionStatus = (data) => {
    saveData({
      url: "/JobRequisition/UpdateJobRequisitionStatus",
      data: data,
    }).then((res) => {
      if (res?.error) {
        SwalService.error("Validation Error", res.error?.data?.message || "Something went wrong!");
      } else {
        SwalService.success("Success", "Requisition status updated successfully!");
        navigate("/job-requisition");
      }
    });
  };

  const getStatusForInterviewerOrHRDraft = (isInterviewer, isHR, isDraft, isRejected) => {
    if ((isInterviewer && (isRejected || isDraft)) || (isHR && isDraft)) {
      return { status: JobRequisitionStatus.PENDING_APPROVAL, level: 2 };
    }
    return null;
  };

  const getStatusForHRPending = (isHR, isPending, currentLevel, formStatus) => {
    if (isHR && isPending && currentLevel === 2) {
      if (formStatus === JobRequisitionStatus.PENDING_APPROVAL) return { status: formStatus, level: 3 };
    }
    return null;
  };

  const getStatusForAdminPending = (isAdmin, isPending, currentLevel, formStatus) => {
    if (isAdmin && isPending && currentLevel === 3) {
      if (formStatus === JobRequisitionStatus.APPROVED) return { status: JobRequisitionStatus.APPROVED, level: currentLevel };
      if (formStatus === JobRequisitionStatus.REJECTED) return { status: JobRequisitionStatus.REJECTED, level: 1 };
    }
    return null;
  };

  const getStatusForHRAApproved = (isHR, isApproved, currentLevel, formStatus) => {
    if (isHR && isApproved && currentLevel === 2) {
      if (formStatus === JobRequisitionStatus.APPROVED) return { status: formStatus, level: 1 };
    }
    return null;
  };

  const getStatusAndLevel = ({ formStatus, currentLevel, isInterviewer, isHR, isAdmin, isDraft, isRejected, isPending, isApproved }) => {
    // Check interviewer/HR draft scenarios
    const interviewerOrHRDraftResult = getStatusForInterviewerOrHRDraft(isInterviewer, isHR, isDraft, isRejected);
    if (interviewerOrHRDraftResult) return interviewerOrHRDraftResult;

    // Check HR pending scenarios
    const hrPendingResult = getStatusForHRPending(isHR, isPending, currentLevel, formStatus);
    if (hrPendingResult) return hrPendingResult;

    // Check admin pending scenarios
    const adminPendingResult = getStatusForAdminPending(isAdmin, isPending, currentLevel, formStatus);
    if (adminPendingResult) return adminPendingResult;

    // Check HR approved scenarios
    const hrApprovedResult = getStatusForHRAApproved(isHR, isApproved, currentLevel, formStatus);
    if (hrApprovedResult) return hrApprovedResult;

    // Default return
    return { status: formStatus, level: currentLevel };
  };

  const syncFormValues = () => {
    if (formRef.current) {
      const currentData = formRef.current.getData();
      if (currentData) {
        setDefaultValue(currentData);
      }
    }
  };

  const onStatusSubmit = () => {
    syncFormValues();
    const formData = formRef.current?.getData();
    if (!formData) return;

    let targetStatus = formData.status;
    if (isAdmin && (isPending || isOnHold) && currentLevel === 3 && formData.status === currentStatus) {
      targetStatus = JobRequisitionStatus.APPROVED;
    }

    const { status, level } = getStatusAndLevel({
      formStatus: targetStatus,
      currentLevel,
      isInterviewer,
      isHR,
      isAdmin,
      isDraft,
      isRejected,
      isPending,
      isApproved,
    });
    updateRequisitionStatus({
      JobRequisitionid: jobRequisitionId || 0,
      Status: status,
      ApprovalLevel: level,
      RejectedReason: formData.rejectionReason,
      JobOpeningId: formData.jobOpeningId,
      dateOfRequest: formData.dateOfRequest ? formData.dateOfRequest.toISOString() : null,
      requiredJoiningDate: formData.requiredJoiningDate ? formData.requiredJoiningDate.toISOString() : null,
    });
  };

  const determineApprovalLevel = () => {
    if (!isEditMode) {
      if (isInterviewer) return 1;
      if (isHR) return 2;
      if (isAdmin) return 3;
      return 1;
    }
    return currentLevel || 1;
  };

  const determineFinalStatus = (formData) => {
    if (!isEditMode) {
      return JobRequisitionStatus.DRAFT;
    }
    if (isAdmin || isHR) {
      return Number(formData?.status) || currentStatus;
    }
    if (isDraft) {
      return JobRequisitionStatus.DRAFT;
    }
    if (isRejected && isCreator) {
      return JobRequisitionStatus.REJECTED;
    }
    return currentStatus;
  };

  const formatDate = (date) => {
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return date;
  };

  const onFormSubmit = () => {
    const formData = formRef.current?.getData();
    if (!formData) return;

    const approvalLevel = determineApprovalLevel();
    const finalStatus = determineFinalStatus(formData);

    const req = {
      ...formData,
      jobRequisitionId: jobRequisitionId || 0,
      status: finalStatus,
      ApprovalLevel: approvalLevel,
      // Convert Date objects to proper format for API (preserving local date)
      dateOfRequest: formatDate(formData.dateOfRequest),
      requiredJoiningDate: formatDate(formData.requiredJoiningDate),
      jobOpeningId: finalStatus === JobRequisitionStatus.APPROVED ? formData.jobOpeningId : null,
      rejectionReason: finalStatus === JobRequisitionStatus.REJECTED ? formData.rejectionReason : null,
    };

    if (jobRequisitionId) {
      updateRequisition(req);
    } else {
      addNewRequisition(req);
    }
  };

  const validationRule = createValidationRule(canEditField);

  const onSave = () => {
    syncFormValues();
    formRef.current?.submit();
  };
  const onCancel = () => navigate("/job-requisition");

  const onCancelRequisition = () => {
    SwalService.confirm("Confirm?", "Are you sure you want to cancel this Job Requisition?").then((confirmed) => {
      if (confirmed) {
        updateRequisitionStatus({
          JobRequisitionid: jobRequisitionId || 0,
          Status: JobRequisitionStatus.CANCELLED,
          ApprovalLevel: 3,
        });
      }
    });
  };

  return (
    <div className="p-fluid">
      <Card className="custom-card">
        <div className="page-header">
          <div className="left-title-part">
            <h2 className="card-title">Manpower Acquisition Request</h2>
          </div>
          <div className="right-btn-part">
            <Button
              type="button"
              icon="pi pi-arrow-left"
              label="Back to Requisitions"
              className="btn secondary-light-btn"
              onClick={onCancel}
            />
            {isEditMode && (
              <Button
                type="button"
                icon="pi pi-history"
                label="History"
                className="btn secondary-btn"
                onClick={() => setShowHistory(true)}
              />
            )}
          </div>
        </div>
        <Sidebar visible={showHistory} header="Requisition History" position="right" onHide={() => setShowHistory(false)} style={{ width: "700px" }}>
          <RequisitionHistory jobRequisitionId={jobRequisitionId} />
        </Sidebar>

        <MolForm key={formKey} ref={formRef} onSubmit={onFormSubmit} initialValues={defaultValue}>
          <ManpowerAcquisitionForm
            departmentOption={departmentOption}
            userOptions={userOptions}
            skillOptions={skillOptions}
            employmentTypeOptions={employmentTypeOptions}
            canEditField={canEditField}
            validationRule={validationRule}
            statusOptions={filteredStatusOptions}
            jobOpening={jobOpening}
            isAdmin={isAdmin}
            isHR={isHR}
            isInterviewer={isInterviewer}
            isInterviewerDraft={isInterviewerDraft}
            jobRequisitionId={jobRequisitionId}
            defaultValue={defaultValue}
            formRef={formRef}
            activeTemplates={activeTemplates}
            fetchTemplateUse={fetchTemplateUse}
            setDefaultValue={setDefaultValue}
            setFormKey={setFormKey}
          />
        </MolForm>
        <div className="bottom-btn-sec">
          <Button label="Cancel" icon="pi pi-times" className="btn secondary-light-btn" onClick={onCancel} />
          {isInterviewer && isDraft && <Button label="Submit for Approval" icon="pi pi-send" className="btn secondary-btn" onClick={onStatusSubmit} loading={isSavingData}/>}
          {isInterviewer && isRejected && <Button label="Resubmit" icon="pi pi-refresh" className="btn secondary-btn" onClick={onStatusSubmit} />}
          {isHR && isDraft && <Button label="Send for Approval" icon="pi pi-send" className="btn secondary-btn" onClick={onStatusSubmit} />}
          {isHR && isPending && currentLevel === 2 && <Button label="Approve (Send to Admin)" icon="pi pi-check" className="btn secondary-btn" onClick={onStatusSubmit} />}
          {isAdmin && isPending && currentLevel === 3 && <Button label="Approve" icon="pi pi-check" className="btn secondary-btn" onClick={onStatusSubmit} />}
          {isHR && isApproved && currentLevel === 2 && <Button label="Submit" icon="pi pi-send" className="btn secondary-btn" onClick={onStatusSubmit} />}
          {isAdmin && isOnHold && currentLevel === 3 && <Button label="Approve" icon="pi pi-check" className="btn secondary-btn" onClick={onStatusSubmit} />}

          {canEditField && (
            <Button
              label={isInterviewer && isDraft ? "Save Draft" : "Save"}
              icon="pi pi-check"
              className="p-button-sm btn secondary-btn"
              onClick={onSave}
              loading={isSavingData}
              disabled={isSavingData}
            />
          )}

          {((isInterviewer && (isDraft || isPending)) || (isHR && (isDraft || (isPending && currentLevel === 2)))) && (
            <Button label="Cancel Requisition" icon="pi pi-ban" className="btn secondary-btn" onClick={onCancelRequisition} />
          )}
        </div>
      </Card>
    </div>
  );
};

export default AddManpowerRequisition;
